import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { MARKET_GROUPS, instrumentsInGroup, tickerOf, type MarketGroup } from "../lib/instruments";
import { PRESETS, PRESET_LIST } from "../lib/presets";
import {
  HORIZONS,
  HORIZON_HINT,
  HORIZON_LABEL,
  TIMEFRAMES,
  type Horizon,
  type Timeframe,
} from "../lib/rounds";
import { isDialogOpen } from "../state/dialogStore";
import { useActiveLoop } from "../state/loops";
import { usePanelStore } from "../state/panelStore";
import { useSettingsStore } from "../state/settingsStore";
import type { Preset } from "../types";
import { Container } from "./Container";
import { Kbd } from "./Kbd";
import { Tooltip } from "./Tooltip";

/**
 * The tucked-away, inline settings panel (spec §4, §9). A toggle reveals a
 * compact strip that reshapes the loop in place: Predict's guess horizon and
 * timeframe, difficulty, markets, Context length, all persisted via the
 * settings store. Mode itself is not here: it lives in the header's Predict /
 * Trade switcher (GRA-94).
 *
 * A control that does not apply to the current Loop is usually disabled with
 * a tooltip saying why, not hidden: a row that disappears when the game
 * changes is harder to find again than one that is visibly unavailable. The
 * one exception is difficulty and Context length under next-candle, which
 * have no effect at all on that guess (GRA-132): those rows are dropped from
 * the strip rather than shown inert, same as the horizon row under Trade.
 * Hiding a row never touches its stored value, so it comes back with
 * whatever it held before the moment the Loop makes it relevant again.
 */

export type Market = { id: string; name: string };

const CONTEXT_CHOICES = [20, 35, 50];

const TITLE_CASE = (s: string) => s[0].toUpperCase() + s.slice(1);

/**
 * What a difficulty preset actually changes, read off PRESETS so the hint can
 * never drift from the numbers the labeling uses: barriers sit at ±m × ATR
 * from the last Context close, and the round resolves by net move if neither
 * barrier is touched within N_max Forward candles.
 */
function presetHint(preset: Preset): string {
  const { m, nMax } = PRESETS[preset];
  return `±${m} ATR, ${nMax} candles`;
}

function Segmented<T extends string | number>({
  label,
  labelTooltip,
  options,
  value,
  onChange,
  format,
  optionTooltip,
  disabledReason,
  optionDisabledReason,
  shortcutKey,
}: {
  label: string;
  /** Optional hint explaining a non-obvious label (e.g. "context"). */
  labelTooltip?: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
  format?: (v: T) => string;
  /** Optional hint per option, for choices whose names hide their mechanics. */
  optionTooltip?: (v: T) => string;
  /** Set to make the whole row inert; the text says why, on every option. */
  disabledReason?: string;
  /** Set to make single options inert; the text says why, on that option. */
  optionDisabledReason?: (v: T) => string | undefined;
  /**
   * The key that cycles this row to its next enabled option. This component
   * only renders while the settings strip is expanded (it sits inside the
   * `hasRows && open` block in SettingsBar), so mounting already implies the
   * strip is open, the same reasoning MarketsRow's digit shortcuts rely on;
   * no separate open/closed check is needed here.
   */
  shortcutKey?: string;
}) {
  // Cycle to the next enabled option, wrapping around and skipping any
  // option disabled via `disabledReason` or `optionDisabledReason`. Mirrors
  // MarketsRow's digit-shortcut listener: same guards, same lifecycle tied
  // to this component's own mount.
  useEffect(() => {
    if (!shortcutKey) return;
    const handler = (event: KeyboardEvent) => {
      if (isDialogOpen()) return;
      const el = event.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA") return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.toLowerCase() !== shortcutKey.toLowerCase()) return;
      event.preventDefault();
      const startIndex = options.indexOf(value);
      const from = startIndex === -1 ? 0 : startIndex;
      for (let step = 1; step <= options.length; step++) {
        const index = (from + step) % options.length;
        const option = options[index];
        const reason = disabledReason ?? optionDisabledReason?.(option);
        if (reason === undefined) {
          onChange(option);
          return;
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shortcutKey, options, value, onChange, disabledReason, optionDisabledReason]);

  return (
    <div className="flex items-center gap-2">
      {labelTooltip ? (
        <Tooltip text={shortcutKey ? `${labelTooltip} · key ${shortcutKey.toUpperCase()}` : labelTooltip} align="left">
          <span className={disabledReason ? "text-muted/50" : "text-muted"}>
            {label}
            {shortcutKey && <Kbd>{shortcutKey.toUpperCase()}</Kbd>}
          </span>
        </Tooltip>
      ) : (
        <span className={disabledReason ? "text-muted/50" : "text-muted"}>
          {label}
          {shortcutKey && <Kbd>{shortcutKey.toUpperCase()}</Kbd>}
        </span>
      )}
      <div className="flex gap-1">
        {options.map((option) => {
          const reason = disabledReason ?? optionDisabledReason?.(option);
          const off = reason !== undefined;
          const button = (
            <button
              key={String(option)}
              type="button"
              aria-pressed={option === value}
              // `aria-disabled`, not `disabled`: a disabled button swallows the
              // pointer events the Tooltip needs, so the hint explaining why the
              // control is off would never appear. The click is ignored here
              // instead, and the button keeps its tab stop so the hint is
              // reachable from the keyboard too.
              aria-disabled={off || undefined}
              onClick={off ? undefined : () => onChange(option)}
              className={`flex min-h-8 min-w-8 items-center justify-center rounded border px-2 py-1 text-xs transition-colors sm:min-h-6 sm:min-w-0 ${
                off
                  ? // Still shows which option is selected, just dimmed: the
                    // setting is inert, not forgotten.
                    option === value
                    ? "cursor-not-allowed border-accent/40 text-accent/50"
                    : "cursor-not-allowed border-muted/20 text-muted/40"
                  : option === value
                  ? "border-accent text-accent"
                  : "border-muted/30 text-muted hover:text-fg"
              }`}
            >
              {format ? format(option) : String(option)}
            </button>
          );
          const hint = reason ?? optionTooltip?.(option);
          return hint ? (
            <Tooltip key={String(option)} text={hint} align="left" interactive>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>
    </div>
  );
}

/**
 * Whether the strip is expanded, held outside the component tree on purpose.
 * Each Loop renders its own SettingsBar inside its own view, so changing the
 * timeframe or the horizon swaps the view and would unmount the strip
 * mid-adjustment: the player clicks "Weekly" and the panel they were working in
 * disappears. Module state survives that remount, so the strip stays open until
 * the player closes it.
 *
 * A thin wrapper over the shared panelStore (GRA-148 follow-up): settings,
 * tools, history and session all read and write one open slot there, so
 * opening any one of them closes whichever other panel was open.
 */
export function useSettingsPanel(): { open: boolean; toggle: () => void } {
  const open = usePanelStore((s) => s.open === "settings");
  const toggle = usePanelStore((s) => s.toggle);
  // Stable: the "," keyboard effect below depends on it.
  const toggleSettings = useCallback(() => toggle("settings"), [toggle]);
  return { open, toggle: toggleSettings };
}

/**
 * How a chip reads: every instrument behind it is drawn from ("on"), none is
 * ("off"), or only some are ("partial", groups only).
 */
type ChipState = "on" | "partial" | "off";

const CHIP_CLASS: Record<ChipState, string> = {
  on: "border-accent text-accent",
  // Half-lit: the group is in the pool, but not all of it.
  partial: "border-accent/60 text-accent/75",
  off: "border-muted/30 text-muted hover:text-fg",
};

/** One market or market-group chip, with its hint. */
function MarketChip({
  label,
  hint,
  state,
  onClick,
  shortcutKey,
}: {
  label: string;
  hint: string;
  state: ChipState;
  onClick: () => void;
  /** The digit that toggles this chip while the strip is expanded. */
  shortcutKey: string;
}) {
  return (
    <Tooltip text={`${hint} · key ${shortcutKey}`} align="left" interactive>
      <button
        type="button"
        aria-pressed={state === "partial" ? "mixed" : state === "on"}
        onClick={onClick}
        className={`flex min-h-8 min-w-8 items-center justify-center rounded border px-2 py-1 transition-colors sm:min-h-6 sm:min-w-0 ${CHIP_CLASS[state]}`}
      >
        {/* `aria-hidden`: a visual hint only, so the chip's accessible name
            stays just the label (the tooltip already spells the key out). */}
        <kbd
          aria-hidden="true"
          className="mr-1.5 hidden rounded border border-current/30 px-1 py-0.5 text-[0.65em] font-mono leading-none opacity-70 sm:inline-block"
        >
          {shortcutKey}
        </kbd>
        {label}
      </button>
    </Tooltip>
  );
}

/** A group narrowed to the instruments the loaded bank actually holds. */
type GroupChips = { id: MarketGroup; label: string; instruments: Market[] };

/**
 * The groups to show, in `MARKET_GROUPS` order, each carrying only the
 * instruments the current bank has Rounds for. Names come from the shared
 * instrument module, not from the bank, so a chip's label and its group can
 * never drift per timeframe; an empty group is dropped rather than offered as a
 * filter that would select nothing.
 */
function groupChips(markets: Market[]): GroupChips[] {
  const present = new Set(markets.map((market) => market.id));
  return MARKET_GROUPS.map((group) => ({
    ...group,
    instruments: instrumentsInGroup(group.id)
      .filter((instrument) => present.has(instrument.id))
      .map((instrument) => ({ id: instrument.id, name: instrument.name })),
  })).filter((group) => group.instruments.length > 0);
}

/** Brief refusal shown when a market deselect is turned away (GRA-129). */
const CHOOSE_ONE_MARKET = "Choose at least one market";

/**
 * The markets filter: an explicit "All" chip plus seven group chips, each a
 * simple toggle for its whole group. Groups are the everyday control (there
 * are 27 instruments; a flat row of them is a wall), and a group only
 * half-selected (from a legacy per-instrument selection still in storage)
 * says so with a half-lit chip.
 *
 * Every chip means one thing (GRA-148): "All" is a shortcut back to the full
 * pool, and a group chip toggles its own group, on or off, whatever the state
 * around it. It used to depend on how the pool was stored: with "All" lit,
 * clicking a group narrowed to that group alone rather than turning it off,
 * so the same click read as "only this" once and "not this" every time after.
 * The row hands the store the pool it is drawn from, which is what lets the
 * store treat an implicitly-full selection as the explicitly-full one.
 *
 * At least one market must always stay selected (GRA-129): turning off the
 * last remaining group, or turning off "All" once every market is selected,
 * is refused rather than silently falling back to the all-markets state, and
 * flashes a brief message explaining why.
 */
function MarketsRow({ markets }: { markets: Market[] }) {
  const settings = useSettingsStore();
  const groups = useMemo(() => groupChips(markets), [markets]);
  // Every instrument a chip in this row can speak for. Not `markets` itself:
  // an instrument the bank has but no group claims cannot be toggled here, so
  // it must not be written into the stored selection either.
  const pool = useMemo(
    () => groups.flatMap((group) => group.instruments.map((m) => m.id)),
    [groups],
  );
  // Empty `markets` means every instrument is in the pool, so every chip reads
  // as on: the stored list is a narrowing, not a checklist to fill in.
  const allMarkets = settings.markets.length === 0;

  // Mirrors ShareButton's flash-then-clear timer.
  const [warning, setWarning] = useState(false);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (warningTimer.current) clearTimeout(warningTimer.current);
    },
    [],
  );

  const flashWarning = () => {
    setWarning(true);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setWarning(false), 1500);
  };

  // Digit shortcuts: "0" for "All", "1".."6" for the groups in the order the
  // chips are drawn in, one key per rendered chip. This row only mounts while
  // the strip is expanded (SettingsBar unmounts it on collapse), so the
  // listener naturally goes quiet with the strip rather than needing its own
  // open/closed check.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (isDialogOpen()) return;
      const el = event.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA") return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === "0") {
        event.preventDefault();
        // No warning when All is already on: pressing 0 there is a no-op the
        // lit chip already explains, unlike deselecting the last group.
        settings.toggleAllMarkets();
        return;
      }
      const index = Number(event.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < groups.length) {
        event.preventDefault();
        const group = groups[index];
        if (!settings.toggleMarkets(group.instruments.map((m) => m.id), pool)) {
          flashWarning();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [groups, pool, settings.toggleAllMarkets, settings.toggleMarkets]);

  if (groups.length === 0) return null;

  const stateOf = (group: GroupChips): ChipState => {
    if (allMarkets) return "on";
    const on = group.instruments.filter((m) => settings.markets.includes(m.id)).length;
    if (on === 0) return "off";
    return on === group.instruments.length ? "on" : "partial";
  };

  return (
    <div className="flex flex-wrap items-start gap-2">
      <span className="py-1 text-muted">markets</span>
      <div className="flex flex-wrap items-center gap-1">
        <MarketChip
          label="All"
          hint="Every market"
          shortcutKey="0"
          state={allMarkets ? "on" : "off"}
          onClick={() => {
            settings.toggleAllMarkets();
          }}
        />
        {groups.map((group, index) => (
          <MarketChip
            key={group.id}
            label={group.label}
            hint={group.instruments.map((m) => tickerOf(m.id)).join(", ")}
            shortcutKey={String(index + 1)}
            state={stateOf(group)}
            onClick={() => {
              if (
                !settings.toggleMarkets(
                  group.instruments.map((m) => m.id),
                  pool,
                )
              ) {
                flashWarning();
              }
            }}
          />
        ))}
      </div>
      {/*
        `basis-full`: always its own line below the chips rather than
        squeezed into whatever width is left beside them, which on a narrow
        viewport wrapped it into a cramped three-line stack. `sm:mt-6` clears
        the refused chip's own hover tooltip on a mouse-driven desktop, which
        floats just below it and would otherwise sit on top of the message's
        first line — the pointer is still hovering the chip right after the
        click that triggered this. A touch tap doesn't leave that lingering
        hover behind, and the settings strip's capped, scrolling height on a
        phone (below) has less room to spare, so mobile keeps the tight gap.
      */}
      {warning && (
        <p role="alert" className="basis-full mt-1 py-1 text-red-400 sm:mt-6">
          {CHOOSE_ONE_MARKET}
        </p>
      )}
    </div>
  );
}

export function SettingsBar({
  markets,
  children,
  historyToggles,
  historyPanels,
}: {
  markets: Market[];
  /**
   * Extra controls sharing the strip's trigger row (the Tools drawer,
   * GRA-131). A drawer passed here renders its trigger as a flex item beside
   * "settings" and its open panel as a `basis-full` sibling below, so a second
   * collapsible group costs no second row of chrome.
   */
  children?: ReactNode;
  /**
   * The history/session toggle buttons (GRA-148): a third pair sharing this
   * same row, pushed to the far right (`ml-auto`) so settings/tools read as
   * one group on the left and history/session as another on the right. Kept
   * separate from `children` so its right alignment doesn't depend on where
   * in JSX order a caller happens to pass the Tools drawer.
   */
  historyToggles?: ReactNode;
  /** What `historyToggles` opens, rendered as a `basis-full` sibling under the row, same as the settings and Tools panels. */
  historyPanels?: ReactNode;
}) {
  const { open, toggle } = useSettingsPanel();
  const settings = useSettingsStore();
  const activeLoop = useActiveLoop();

  // Quick settings toggle: "," (comma), which doesn't collide with guess/tool keys.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // The quick-settings toggle goes quiet while a dialog is open (GRA-146).
      if (isDialogOpen()) return;
      const el = event.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA") return;
      // No modifier keys (matches the history/session/tools shortcuts,
      // GRA-148): otherwise a browser or OS chord that happens to end in ","
      // would also flip this panel open.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === ",") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  // A Loop that fixes every play parameter (the Daily challenge, GRA-139) has
  // no rows left, and a "settings" disclosure that opens onto nothing is worse
  // than no disclosure: the "settings" trigger, its panel, and the Tools
  // drawer that rides beside it all drop out rather than lying about there
  // being something under them. History/session are unaffected: they read
  // the app-wide round log, not this Loop's settings, so they stay in the row
  // on their own for a Loop like the challenge that has nothing else to show.
  const hasRows =
    activeLoop.settings.horizon ||
    activeLoop.settings.timeframe ||
    activeLoop.settings.difficulty !== "hidden" ||
    activeLoop.settings.contextLength !== "hidden" ||
    markets.length > 0;
  if (!hasRows && !historyToggles) return null;

  return (
    <div className="border-b border-muted/20 py-1 font-mono text-sm">
      <Container>
        {/* Baseline is not repeated here: it lives on the accuracy tooltip in
            the header's stats (GRA-95). One row for all four collapsibles
            (GRA-148): settings and tools read as a group on the left,
            history and session (`ml-auto`) as a group on the right, wrapping
            onto a second line only if a narrow viewport truly can't fit all
            four at once. */}
        <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-5">
          {hasRows && (
            <button
              type="button"
              onClick={toggle}
              className="flex min-h-8 items-center text-xs text-muted hover:text-accent sm:min-h-6"
              aria-expanded={open}
            >
              {open ? "▾ settings" : "▸ settings"}
              <Kbd>,</Kbd>
            </button>
          )}
          {hasRows && children}
          {historyToggles && (
            <div className="ml-auto flex items-center gap-x-2 sm:gap-x-5">{historyToggles}</div>
          )}
        </div>

        {hasRows && open && (
          // On a phone the expanded strip (five groups, plus the market group
          // chips) is tall enough to crush the chart below it, so it is capped
          // and scrolls in place; from `sm` it lays out in full as before.
          <div className="flex max-h-[45vh] flex-wrap items-center gap-x-6 gap-y-2 overflow-y-auto py-2 text-xs sm:max-h-none sm:overflow-y-visible">
            {/* Mode lives in the header switcher (GRA-94), not here. */}
            {activeLoop.settings.horizon && (
              <Segmented<Horizon>
                label="guess"
                labelTooltip="what you are calling"
                options={HORIZONS}
                value={settings.horizon}
                onChange={settings.setHorizon}
                format={(h) => HORIZON_LABEL[h]}
                optionTooltip={(h) => HORIZON_HINT[h]}
                // Both horizons play on every timeframe (ADR-0013): every bank
                // stores the Forward path the trend game reads.
                shortcutKey="n"
              />
            )}
            {activeLoop.settings.timeframe && (
              <Segmented<Timeframe>
                label="timeframe"
                options={TIMEFRAMES}
                value={settings.timeframe}
                onChange={settings.setTimeframe}
                format={TITLE_CASE}
                shortcutKey="i"
              />
            )}
            {/*
              Hidden, not disabled, for next-candle (GRA-132): that guess
              reads no barrier and no preset, so the row would sit there
              doing nothing. It reappears, with whatever value it held
              before, the moment a Loop that reads it comes back.
            */}
            {activeLoop.settings.difficulty !== "hidden" && (
              <Segmented<Preset>
                label="difficulty"
                labelTooltip="how far price must move, and by when"
                options={PRESET_LIST}
                value={settings.preset}
                onChange={settings.setPreset}
                format={TITLE_CASE}
                optionTooltip={presetHint}
                shortcutKey="p"
              />
            )}
            {/*
              Context length applies to Trade too (GRA-90): both loops read
              this setting when selecting a Round, clamped to what the
              timeframe's bank actually stores (settingsStore). The
              next-candle horizon charts the whole stored Context regardless of
              this setting, so the row is hidden there too (GRA-132).
            */}
            {activeLoop.settings.contextLength !== "hidden" && (
              <Segmented<number>
                label="context"
                labelTooltip="candles shown before you guess"
                options={CONTEXT_CHOICES}
                value={settings.contextLength}
                onChange={settings.setContextLength}
                shortcutKey="c"
              />
            )}

            {/* Markets apply to both modes and both Predict timeframes (GRA-90, GRA-95). */}
            {markets.length > 0 && <MarketsRow markets={markets} />}
          </div>
        )}

        {historyPanels}
      </Container>
    </div>
  );
}
